import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import ExecutiveDashboard from "../../Executive/ExecutiveDashboard";
import {
  CORE_METRIC_FALLBACK_LABELS,
  CORE_METRIC_IDS,
  formatMetric,
  metricNumber,
  useMetricsWithCompare,
  useMonthlyTrend,
} from "../useMetrics";
import DeltaChip from "../DeltaChip";
import Sparkline from "../Sparkline";
import { TabProps } from "../types";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";
const TILE_ACCENTS = [
  PRIMARY,
  "#3F51B5",
  "#2e7d32",
  "#fdb814",
  "#00838f",
  "#8e24aa",
  "#e64a19",
  "#455a64",
  "#c2185b",
];

interface OverviewTabProps extends TabProps {
  isAdmin: boolean;
  /** Compare-to-previous-period toggle from the filter bar. */
  compare?: boolean;
  previousRange?: { start: Dayjs | null; end: Dayjs | null } | null;
  previousLabel?: string;
}

/**
 * Admins get the leadership view (the existing Executive dashboard). Everyone
 * else gets a personal scorecard of the nine core metrics — the server scopes
 * /reports/metrics to the caller when they are not an admin, so the sparkline's
 * monthly call is already self-scoped too.
 */
const OverviewTab: React.FC<OverviewTabProps> = ({
  isAdmin,
  start,
  end,
  consultant,
  onDrill,
  compare = false,
  previousRange = null,
  previousLabel,
}) => {
  const { current, previous } = useMetricsWithCompare(
    { start, end, consultant, groupBy: "none", enabled: !isAdmin },
    compare ? previousRange : null,
  );
  const trend = useMonthlyTrend({ start, end, consultant, enabled: !isAdmin });

  const { data, loading, error } = current;

  if (isAdmin) {
    return <ExecutiveDashboard embedded start={start} end={end} />;
  }

  const catalogById = new Map(
    (data?.catalog || []).map((entry) => [entry.id, entry]),
  );
  const showCompare = compare && Boolean(previous.data);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34", mb: 2 }}>
        My scorecard
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={24} sx={{ color: PRIMARY }} />
        </Stack>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {CORE_METRIC_IDS.map((id, index) => {
          const entry = catalogById.get(id);
          const label = entry?.label || CORE_METRIC_FALLBACK_LABELS[id] || id;
          const accent = TILE_ACCENTS[index % TILE_ACCENTS.length];
          const value = metricNumber(data?.total, id);
          const series = trend.available ? trend.series(id) : [];
          return (
            <Box
              key={id}
              onClick={() => onDrill({ id, label })}
              sx={{
                flex: "1 1 180px",
                cursor: "pointer",
                "&:hover": { transform: "translateY(-2px)" },
                transition: "transform 0.15s ease",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: "100%",
                  borderRadius: "16px",
                  border: CARD_BORDER,
                  borderTop: `3px solid ${accent}`,
                  backgroundColor: "white",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 800,
                    color: "#2c2c34",
                    lineHeight: 1.1,
                  }}
                >
                  {data ? formatMetric(value, entry?.unit) : "—"}
                </Typography>

                {showCompare && data && (
                  <Box sx={{ mt: 0.5 }}>
                    <DeltaChip
                      current={value}
                      previous={metricNumber(previous.data?.total, id)}
                      unit={entry?.unit}
                      periodLabel={previousLabel}
                    />
                  </Box>
                )}

                <Typography
                  variant="caption"
                  sx={{
                    color: "#8a8a93",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontWeight: 600,
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  {label}
                </Typography>

                {entry?.definition ? (
                  <Typography variant="caption" sx={{ color: "#b0b0b8" }}>
                    {entry.definition}
                  </Typography>
                ) : null}

                {series.length > 1 && (
                  <Box
                    sx={{
                      mt: 1,
                      color: accent,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Sparkline values={series} labels={trend.labels} title={label} />
                  </Box>
                )}
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default OverviewTab;
